"use client";

import { Check } from "iconoir-react";
import { useState } from "react";

import {
  EMAIL_CAPTURE_PILL_FORM_CLASS,
  EMAIL_CAPTURE_PILL_INPUT_CLASS,
} from "@/components/forms/email-capture-pill-styles";
import { useHubspotContext } from "@/components/forms/hubspot_context";
import { ArrowCtaButton } from "@/components/ui/arrow-cta-button";
import { cn } from "@/lib/utils";

const HUBSPOT_FORM_GUID = "bb627350-91a7-4318-be81-81b563d17118";
const FORM_NAME = "Research report download";

/** Figma 7081:66269 — grey-100 rounded badge with filled check (light CTA). */
function ResearchNoSignupCheck({ theme }: { theme: "light" | "dark" }) {
  if (theme === "dark") {
    // Figma 8083:19809 — 20px stroke check on hero gradient.
    return (
      <Check
        className="text-grey-0 size-5 shrink-0"
        strokeWidth={1.5}
        aria-hidden
      />
    );
  }

  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
      className="shrink-0"
    >
      <rect width="16" height="16" rx="4" className="fill-grey-100" />
      <path
        d="M4.94348 7.68934L6.84626 9.59212L11.0591 5.37926L11.8737 6.19384L6.84626 11.2213L4.12891 8.50391L4.94348 7.68934Z"
        className="fill-grey-950"
      />
    </svg>
  );
}

interface ResearchEmailDownloadProps {
  emailPlaceholder: string;
  submitLabel: string;
  noSignupLabel?: string;
  className?: string;
  /** Compact pill for hero; stacked layout for mid-page CTA. */
  variant?: "pill" | "stacked";
  theme?: "light" | "dark";
}

export function ResearchEmailDownload({
  emailPlaceholder,
  submitLabel,
  noSignupLabel,
  className,
  variant = "pill",
  theme = "dark",
}: ResearchEmailDownloadProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const hubspotContext = useHubspotContext();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const email = String(data.get("email") ?? "").trim();
    if (!email) return;

    setSubmitting(true);
    setError(false);
    try {
      const res = await fetch("/api/submit-hubspot-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          guid: HUBSPOT_FORM_GUID,
          formName: FORM_NAME,
          data: { email },
          hubspotContext: { ...hubspotContext, pageName: FORM_NAME },
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <p
        className={cn(
          "text-ds-body-sm",
          theme === "dark" ? "text-grey-0" : "text-grey-950",
          className
        )}
      >
        Thanks — check your inbox for the report.
      </p>
    );
  }

  const formInner =
    variant === "pill" ? (
      <form
        onSubmit={handleSubmit}
        className={cn(
          EMAIL_CAPTURE_PILL_FORM_CLASS,
          "border-grey-300 bg-grey-0 max-w-[520px]",
          "focus-within:border-grey-500 transition-colors"
        )}
      >
        <label htmlFor="research-email" className="sr-only">
          {emailPlaceholder}
        </label>
        <input
          id="research-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder={emailPlaceholder}
          className={EMAIL_CAPTURE_PILL_INPUT_CLASS}
        />
        <ArrowCtaButton type="submit" layout="hero-form" disabled={submitting}>
          {submitLabel}
        </ArrowCtaButton>
      </form>
    ) : (
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-[480px] flex-col gap-4"
      >
        <div
          className={cn(
            EMAIL_CAPTURE_PILL_FORM_CLASS,
            "border-grey-300 bg-grey-0"
          )}
        >
          <label htmlFor="research-cta-email" className="sr-only">
            {emailPlaceholder}
          </label>
          <input
            id="research-cta-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder={emailPlaceholder}
            className={EMAIL_CAPTURE_PILL_INPUT_CLASS}
          />
          <ArrowCtaButton
            type="submit"
            layout="hero-form"
            disabled={submitting}
          >
            {submitLabel}
          </ArrowCtaButton>
        </div>
      </form>
    );

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {formInner}
      {noSignupLabel ? (
        <p
          className={cn(
            "text-ds-label-lg flex items-center gap-2 font-medium",
            theme === "dark" ? "text-grey-0" : "text-grey-950"
          )}
        >
          <ResearchNoSignupCheck theme={theme} />
          {noSignupLabel}
        </p>
      ) : null}
      {error ? (
        <p className="text-ds-body-xs text-red-300">
          Something went wrong. Please try again.
        </p>
      ) : null}
    </div>
  );
}
